import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, AlertCircle, Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useProspects } from "../../context/ProspectsContext";
import { HOME_COORD } from "../../constants/config";
import { addOfflineRequest } from "../../lib/offlineQueue";

export default function AddProspect() {
  const navigate = useNavigate();
  const { addProspect } = useProspects();

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [unitInterest, setUnitInterest] = useState("");

  const [coord, setCoord] = useState(null);
  const [gpsError, setGpsError] = useState(null);
  const [saving, setSaving] = useState(false);

  // Get GPS as soon as page loads
  useEffect(() => {
    if (!navigator.geolocation) {
      setGpsError("Browser tidak mendukung GPS.");
      setCoord({ ...HOME_COORD, accuracy: 0, mocked: true });
      return;
    }
    const id = navigator.geolocation.getCurrentPosition(
      (pos) => {
        setCoord({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracy: pos.coords.accuracy,
        });
        setGpsError(null);
      },
      (err) => {
        setGpsError(err.message || "Gagal mengambil lokasi.");
        setCoord({ ...HOME_COORD, accuracy: 0, mocked: true });
      },
      { enableHighAccuracy: true, timeout: 8000, maximumAge: 0 }
    );
    return () => id;
  }, []);

  const handleSave = async () => {
    if (!name.trim()) return toast.error("Nama wajib diisi");
    if (!phone.trim() || phone.replace(/\D/g, "").length < 8)
      return toast.error("Nomor HP tidak valid");
    if (!unitInterest.trim()) return toast.error("Tipe motor diminati wajib diisi");

    setSaving(true);
    
    // OFFLINE HANDLING
    if (!navigator.onLine) {
      try {
        const payload = {
          name: name.trim(),
          phone: phone.trim(),
          address: `Lokasi GPS · ${(coord?.lat ?? 0).toFixed(4)}, ${(coord?.lng ?? 0).toFixed(4)}`,
          coord: { lat: coord?.lat ?? HOME_COORD.lat, lng: coord?.lng ?? HOME_COORD.lng },
          interested_product: unitInterest.trim(),
          status: "new",
          notes: "Prospek baru ditambahkan dari mobile app (Offline).",
        };
        await addOfflineRequest("/prospects", payload, null);
        toast.success("Offline: Prospek masuk antrean sinkronisasi!");
        navigate("/sales/prospects");
      } catch (err) {
        toast.error("Gagal menyimpan ke penyimpanan lokal.");
      } finally {
        setSaving(false);
      }
      return;
    }

    // ONLINE HANDLING
    try {
      const newP = await addProspect({
        name: name.trim(),
        phone: phone.trim(),
        address: `Lokasi GPS · ${(coord?.lat ?? 0).toFixed(4)}, ${(coord?.lng ?? 0).toFixed(4)}`,
        coord: { lat: coord?.lat ?? HOME_COORD.lat, lng: coord?.lng ?? HOME_COORD.lng },
        interested_product: unitInterest.trim(),
        status: "new",
        notes: "Prospek baru ditambahkan dari mobile app.",
        photo: null,
      });
      window.dispatchEvent(new CustomEvent("hscis:prospect-saved", { detail: newP }));
      toast.success("Prospek berhasil disimpan!");
      navigate(`/sales/prospects/${newP.id}`);
    } catch (err) {
      if (err.message === "Network Error" || err.code === "ERR_NETWORK") {
        const payload = {
          name: name.trim(),
          phone: phone.trim(),
          address: `Lokasi GPS · ${(coord?.lat ?? 0).toFixed(4)}, ${(coord?.lng ?? 0).toFixed(4)}`,
          coord: { lat: coord?.lat ?? HOME_COORD.lat, lng: coord?.lng ?? HOME_COORD.lng },
          interested_product: unitInterest.trim(),
          status: "new",
          notes: "Prospek baru ditambahkan dari mobile app (Offline).",
        };
        await addOfflineRequest("/prospects", payload, null);
        toast.success("Offline: Prospek masuk antrean sinkronisasi!");
        navigate("/sales/prospects");
      } else {
        toast.error(err?.response?.data?.detail || "Gagal menyimpan prospek.");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="add-prospect-page" className="min-h-full bg-honda-bg pb-6">
      {/* Header */}
      <div className="sticky top-0 z-30 bg-white border-b border-honda-border px-5 pt-12 pb-3 flex items-center gap-3">
        <button
          data-testid="add-back-button"
          onClick={() => navigate(-1)}
          className="w-9 h-9 rounded-full hover:bg-honda-bg flex items-center justify-center -ml-1.5 transition-colors"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="flex-1">
          <h1 className="font-heading font-bold text-lg text-honda-ink leading-tight">
            Tambah Prospek
          </h1>
          <p className="text-[11px] text-honda-muted">Data Prospek Baru</p>
        </div>
      </div>

      {/* GPS status pill */}
      <div className="px-5 pt-3">
        <div
          data-testid="gps-status-pill"
          className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[11px] font-semibold ${
            coord && !coord.mocked
              ? "bg-green-100 text-green-700"
              : coord?.mocked
              ? "bg-yellow-100 text-yellow-800"
              : "bg-honda-bg text-honda-muted"
          }`}
        >
          <MapPin size={12} />
          {!coord && "Mengambil lokasi GPS..."}
          {coord && !coord.mocked && (
            <>GPS Terkunci · {coord.lat.toFixed(5)}, {coord.lng.toFixed(5)}</>
          )}
          {coord?.mocked && (
            <>Mode Demo · {coord.lat.toFixed(5)}, {coord.lng.toFixed(5)}</>
          )}
        </div>
        {gpsError && (
          <p className="mt-1 text-[11px] text-yellow-700 flex items-center gap-1">
            <AlertCircle size={11} /> {gpsError}
          </p>
        )}
      </div>

      {/* Form */}
      <div className="px-5 mt-4 space-y-4 animate-fade-in">
        <div>
          <label className="honda-label">Nama Prospek</label>
          <input
            data-testid="prospect-name-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="cth. Bapak Hendrawan"
            className="honda-input mt-1.5"
          />
        </div>
        <div>
          <label className="honda-label">Nomor HP</label>
          <input
            data-testid="prospect-phone-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+62 812-xxxx-xxxx"
            type="tel"
            inputMode="tel"
            className="honda-input mt-1.5"
          />
        </div>
        <div>
          <label className="honda-label">Type Motor Diminati</label>
          <input
            data-testid="prospect-unit-input"
            value={unitInterest}
            onChange={(e) => setUnitInterest(e.target.value)}
            placeholder="Contoh: Beat Street / Scoopy / Vario 160 / PCX 160"
            className="honda-input mt-1.5"
          />
        </div>

        <button
          data-testid="save-prospect-button"
          onClick={handleSave}
          disabled={saving}
          className="w-full honda-btn-primary py-3 mt-6 flex items-center justify-center gap-2 disabled:opacity-60"
        >
          {saving ? (
            <>
              <Loader2 className="animate-spin" size={16} /> Menyimpan...
            </>
          ) : (
            <>
              <Check size={16} /> Simpan Prospek
            </>
          )}
        </button>
      </div>
    </div>
  );
}
